<template>
  <div class="project-selector relative">
    <!-- Project combobox (visible when logged in with 1 or more projects) - Desktop -->
    <div v-if="isAuthenticated && projects.length >= 1" class="relative project-dropdown hidden md:block" ref="projectDropdownRef">
      <button
        @click="toggleProjectDropdown"
        :class="[
          'text-[15px] leading-7 text-white font-archivo font-medium flex items-center gap-1',
          projects.length <= 1 ? 'cursor-default' : 'cursor-pointer'
        ]"
        :disabled="isLoading"
        style="max-width: 250px;"
      >
        <span
          v-if="selectedProject"
          class="truncate max-w-[210px]"
          :title="selectedProject.name || selectedProject.nome"
        >
          {{ (selectedProject.name || selectedProject.nome).length > 20 ? (selectedProject.name || selectedProject.nome).substring(0, 20) + '...' : (selectedProject.name || selectedProject.nome) }}
        </span>
        <span v-else>{{ $t('project.selectProject') }}</span>
        <svg
          v-if="projects.length > 1"
          class="w-4 h-4 flex-shrink-0"
          :class="{ 'transform rotate-180': isProjectDropdownOpen }"
          viewBox="0 0 24 24"
          fill="#FFDD00"
        >
          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
        </svg>
      </button>

      <!-- Dropdown Desktop -->
      <div v-if="isProjectDropdownOpen" class="absolute top-full left-0 z-50">
        <div class="menu-level bg-black py-1 max-h-[500px] overflow-y-auto border-0 border-none" style="width: 250px; background-color: #000000;">
          <!-- Loading State -->
          <div v-if="isLoading" class="px-4 py-2 text-empire-yellow">
            {{ $t('common.loading') }}...
          </div>

          <!-- Projects List -->
          <template v-else>
            <div
              v-for="project in projects"
              :key="project.id"
              class="project-item"
            >
              <div
                :class="[
                  'flex items-center justify-between px-4 py-2 cursor-pointer transition-colors duration-200 group font-medium tracking-wide whitespace-nowrap overflow-hidden truncate',
                  selectedProject && selectedProject.id === project.id ? 'bg-empire-yellow text-black' : 'text-white hover:bg-empire-yellow hover:text-black'
                ]"
                :title="project.name || project.nome"
                @click="selectProject(project)"
              >
                <!-- Project Name -->
                {{ (project.name || project.nome).length > 20 ? (project.name || project.nome).substring(0, 20) + '...' : (project.name || project.nome) }}
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Mobile Version -->
    <div v-if="isAuthenticated && projects.length >= 1" class="relative project-dropdown md:hidden" ref="projectDropdownRefMobile">
      <button
        @click="toggleProjectDropdown"
        :class="[
          'px-1 py-1 text-[13px] leading-6 text-white font-archivo font-medium text-center flex items-center justify-center gap-1',
          projects.length <= 1 ? 'cursor-default' : 'cursor-pointer'
        ]"
        :disabled="isLoading"
        style="max-width: 120px; min-width: 80px;"
      >
        <span v-if="selectedProject" class="truncate max-w-[80px]" :title="selectedProject.name || selectedProject.nome">
          {{ (selectedProject.name || selectedProject.nome).length > 10 ? (selectedProject.name || selectedProject.nome).substring(0, 10) + '...' : (selectedProject.name || selectedProject.nome) }}
        </span>
        <span v-else class="truncate max-w-[80px]">{{ $t('project.selectProject') }}</span>
        <svg
          v-if="projects.length > 1"
          class="w-3 h-3 flex-shrink-0 transition-transform duration-200"
          :class="{ 'transform rotate-180': isProjectDropdownOpen }"
          viewBox="0 0 24 24"
          fill="#FFDD00"
        >
          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
        </svg>
      </button>

      <!-- Dropdown Mobile -->
      <div
        v-if="isProjectDropdownOpen"
        class="fixed z-50 bg-black shadow-lg rounded-lg mt-2 mobile-project-menu"
        :style="dropdownMobileStyle"
      >
        <div class="max-h-[40vh] overflow-y-auto py-1" style="width: 180px;">
          <div
            v-for="project in projects"
            :key="project.id"
            class="relative project-item"
          >
            <div
              :class="['flex items-center justify-between px-3 py-2 cursor-pointer transition-colors duration-200 group font-medium text-[13px] leading-5 mobile-project-item', selectedProject && selectedProject.id === project.id ? 'bg-empire-yellow text-black' : 'text-white hover:bg-empire-yellow hover:text-black']"
              @click="selectProject(project)"
            >
              <!-- Project name -->
              <span
                class="truncate max-w-[140px]"
                :title="project.name || project.nome"
              >
                {{ (project.name || project.nome).length > 15 ? (project.name || project.nome).substring(0, 15) + '...' : (project.name || project.nome) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation modal for switching projects -->
    <div v-if="showConfirmationModal" class="fixed inset-0 z-50 flex items-center justify-center">
      <!-- Overlay -->
      <div class="absolute inset-0 bg-black bg-opacity-50" @click="cancelProjectChange"></div>

      <!-- Modal Content -->
      <div class="relative bg-white p-8 rounded-lg shadow-lg w-full max-w-md">
        <h2 class="font-archivo-narrow font-semibold text-2xl mb-6 text-center">{{ $t('project.changeConfirmation.title') }}</h2>

        <p class="mb-6 text-center">
          {{ $t('project.changeConfirmation.message') }}
        </p>

        <div class="flex flex-col gap-4">
          <button
            @click="confirmProjectChange"
            class="w-full bg-empire-yellow py-4 font-archivo-narrow font-semibold text-xl text-black"
          >
            {{ $t('project.changeConfirmation.confirm') }}
          </button>

          <button
            @click="cancelProjectChange"
            class="w-full bg-white border border-gray-300 py-4 font-archivo-narrow font-semibold text-xl text-black"
          >
            {{ $t('project.changeConfirmation.cancel') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Toast Message -->
    <div
      v-if="showToast"
      class="fixed bottom-4 right-4 bg-black text-empire-yellow px-6 py-4 rounded-md shadow-lg z-50 transition-opacity duration-300"
      :class="{ 'opacity-0': !showToast, 'opacity-100': showToast }"
    >
      <p class="font-archivo-narrow text-lg">
        {{ toastMessage }}
      </p>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, onUnmounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { projectService } from '@/services/projectService'
import eventBus from '@/utils/eventBus'

export default {
  name: 'ProjectSelector',
  props: {
    isAuthenticated: {
      type: Boolean,
      required: true
    }
  },
  setup(props) {
    const { t } = useI18n()

    const projects = ref([])
    const isLoading = ref(false)
    const selectedProjectId = ref('')
    const tempSelectedProjectId = ref('')
    const showConfirmationModal = ref(false)
    const isProjectDropdownOpen = ref(false)
    const showToast = ref(false)
    const toastMessage = ref('')
    const projectDropdownRef = ref(null)
    const projectDropdownRefMobile = ref(null)
    const selectedProject = ref(null)

    // Mobile detection and dropdown positioning for mobile
    const isMobile = ref(window.innerWidth < 768)
    const handleResize = () => { isMobile.value = window.innerWidth < 768 }

    const dropdownMobileStyle = computed(() => {
      if (!projectDropdownRefMobile.value || !isProjectDropdownOpen.value) {
        return {}
      }
      const rect = projectDropdownRefMobile.value.getBoundingClientRect()
      const dropdownWidth = 180
      const dropdownMaxHeight = window.innerHeight * 0.4
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      let left = rect.left + (rect.width / 2) - (dropdownWidth / 2)
      let top = rect.bottom + 8
      if (left < 8) left = 8
      if (left + dropdownWidth > viewportWidth - 8) left = viewportWidth - dropdownWidth - 8
      if (top + dropdownMaxHeight > viewportHeight - 8) {
        top = rect.top - dropdownMaxHeight - 8
        if (top < 8) top = 8
      }
      return { left: `${left}px`, top: `${top}px` }
    })

    // Loads the user projects
    const loadProjects = async () => {
      if (!props.isAuthenticated) return

      isLoading.value = true

      try {
        // Fetches the logged-in user projects using the JWT token
        const userProjects = await projectService.getCurrentUserProjects()
        // Filters out projects without a name or description
        projects.value = userProjects.filter(project => {
          const name = project.name || project.nome || ''
          return name.trim().length > 0
        })

        // Loads the selected project from sessionStorage
        const savedProject = projectService.getSelectedProject()

        // Checks whether the saved project exists in the user project list
        if (savedProject && savedProject.id) {
          const projectExists = projects.value.some(p => p.id === savedProject.id)

          if (projectExists) {
            selectedProjectId.value = savedProject.id
            selectedProject.value = savedProject
            console.log('Projeto carregado do sessionStorage:', selectedProject.value)
          } else {
            // If the saved project is not in the filtered list, keep the project from sessionStorage
            // (it may have been filtered out for having no name, but it is still valid)
            selectedProjectId.value = savedProject.id
            selectedProject.value = savedProject
            console.log('Projeto do sessionStorage mantido (não encontrado na lista filtrada):', selectedProject.value)
          }
        } else if (projects.value.length > 0) {
          // If there is no selected project, select the first one in the list
          selectedProjectId.value = projects.value[0].id
          selectedProject.value = {
            id: projects.value[0].id,
            name: projects.value[0].name || projects.value[0].nome
          }
          projectService.saveSelectedProject(selectedProject.value)
          console.log('Nenhum projeto salvo, selecionando o primeiro:', selectedProject.value)
        }
      } catch (err) {
        console.error('Erro ao carregar projetos:', err)
      } finally {
        isLoading.value = false
      }
    }

    // Toggle do dropdown de projetos
    const toggleProjectDropdown = (event) => {
      event.stopPropagation()

      // If there is only 1 project, do not open the dropdown
      if (projects.value.length <= 1) {
        return
      }

      isProjectDropdownOpen.value = !isProjectDropdownOpen.value

      // If the dropdown is being opened, emit an event to close the other dropdowns
      if (isProjectDropdownOpen.value) {
        eventBus.emit('dropdown-opened', 'project')
      }
    }

    // Method to close the project dropdown
    const closeDropdown = () => {
      if (isProjectDropdownOpen.value) {
        isProjectDropdownOpen.value = false
      }
    }

    // Function to close the dropdown when clicking outside
    const handleClickOutside = (event) => {
      const projectDropdown = event.target.closest('.project-dropdown')

      // Closes the project menu only when clicking outside of it
      if (!projectDropdown) {
        isProjectDropdownOpen.value = false
      }
    }

    // Adds the event listener when the component is mounted
    onMounted(() => {
      document.addEventListener('click', handleClickOutside)
      window.addEventListener('resize', handleResize)
    })

    // Removes the event listener when the component is unmounted
    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
    })

    // Selects a project
    const selectProject = (project) => {
      // Checks whether the selected project is the same one already selected
      if (selectedProjectId.value === project.id) {
        // Only closes the dropdown without showing the modal
        isProjectDropdownOpen.value = false
        return
      }

      // If it is a different project, show the confirmation modal
      tempSelectedProjectId.value = project.id
      isProjectDropdownOpen.value = false
      showConfirmationModal.value = true
    }

    // Handles the project change (for compatibility with the previous select)
    const handleProjectChange = (event) => {
      tempSelectedProjectId.value = event.target.value
      showConfirmationModal.value = true
    }

    // Shows the custom toast
    const showSuccessToast = (message) => {
      toastMessage.value = message
      showToast.value = true
      setTimeout(() => {
        showToast.value = false
      }, 3000) // The toast disappears after 3 seconds
    }

    // Confirms the project change
    const confirmProjectChange = async () => {
      try {
        isLoading.value = true

        // Finds the selected project
        const project = projects.value.find(p => p.id === tempSelectedProjectId.value)
        if (!project) {
          throw new Error('Projeto não encontrado')
        }

        // Updates the selected project
        selectedProject.value = {
          id: project.id,
          name: project.name || project.nome
        }

        // Saves the newly selected project
        // The service emits the 'selected-project-changed' event
        projectService.saveSelectedProject(selectedProject.value)

        // Updates the selected project ID
        selectedProjectId.value = tempSelectedProjectId.value

        // Shows a success message
        showSuccessToast(t('project.changeConfirmation.success'))

        // Closes the modal
        showConfirmationModal.value = false
      } catch (err) {
        console.error('Erro ao mudar de projeto:', err)
        showSuccessToast(t('project.changeConfirmation.error'))
      } finally {
        isLoading.value = false
      }
    }

    // Cancels the project change
    const cancelProjectChange = () => {
      // Restores the previous value
      tempSelectedProjectId.value = selectedProjectId.value
      // Closes the modal
      showConfirmationModal.value = false
    }

    // Watches authentication changes
    watch(() => props.isAuthenticated, (newValue) => {
      if (newValue) {
        loadProjects()

        // Checks sessionStorage after a small delay to make sure the projects were loaded
        setTimeout(() => {
          checkSessionStorageProject()
        }, 500)
      } else {
        projects.value = []
        selectedProjectId.value = ''
      }
    })

    // Function to check and update the selected project straight from sessionStorage
    const checkSessionStorageProject = async () => {
      if (!props.isAuthenticated) return false

      const savedProject = projectService.getSelectedProject()

      if (savedProject && savedProject.id) {
        // If the project list is empty, reload it first
        if (projects.value.length === 0) {
          console.log('Lista de projetos vazia ao verificar sessionStorage, recarregando...')
          await loadProjects()
        }
        
        // Checks whether the project exists in the project list
        const projectExists = projects.value.some(p => p.id === savedProject.id)

        if (projectExists) {
          // Updates the selected project directly
          selectedProjectId.value = savedProject.id
          selectedProject.value = savedProject
          console.log('Projeto atualizado do sessionStorage:', selectedProject.value)
          return true
        } else {
          // If it was not found in the list but exists in sessionStorage, update it anyway
          // (the project may not be in the filtered list yet)
          selectedProjectId.value = savedProject.id
          selectedProject.value = savedProject
          console.log('Projeto atualizado do sessionStorage (não encontrado na lista):', selectedProject.value)
          return true
        }
      }

      return false
    }

    let sessionIntervalId = null

    // Loads the projects when the component is mounted
    onMounted(() => {
      if (props.isAuthenticated) {
        loadProjects()

        // Checks sessionStorage after a small delay to make sure the projects were loaded
        setTimeout(async () => {
          await checkSessionStorageProject()
        }, 500)

        // Sets an interval to periodically check the project in sessionStorage
        sessionIntervalId = setInterval(async () => {
          if (props.isAuthenticated) {
            await checkSessionStorageProject()
          }
        }, 2000) // Checks every 2 seconds
      }

      // Adds an event listener to detect sessionStorage changes
      window.addEventListener('storage', (event) => {


        if (event.key === 'selectedProject' && props.isAuthenticated) {
          console.log('Projeto alterado no sessionStorage, verificando...')
          // Checks sessionStorage directly
          checkSessionStorageProject().then(success => {
            if (!success) {
              // If the project was not found, reload all the projects
              console.log('Projeto não encontrado no sessionStorage, recarregando projetos...')
              loadProjects()
            }
          })
        }
      })

      // Adds a listener for the project-changed event
      eventBus.on('project-changed', async (projectData) => {
        console.log('Evento project-changed recebido:', projectData)
        if (props.isAuthenticated && projectData && projectData.id) {
          // If the project list is empty, reload it first
          if (projects.value.length === 0) {
            console.log('Lista de projetos vazia, recarregando...')
            await loadProjects()
          }
          
          // Checks whether the project exists in the project list
          const projectExists = projects.value.some(p => p.id === projectData.id)
          console.log('Projeto existe na lista?', projectExists, 'ID:', projectData.id)

          if (projectExists) {
            // Updates the selected project directly without reloading every project
            selectedProjectId.value = projectData.id
            selectedProject.value = projectData
            console.log('Projeto atualizado via evento:', selectedProject.value)
          } else {
            // If the project is not in the list, reload the projects and try again
            console.log('Projeto não encontrado na lista, recarregando projetos...')
            await loadProjects()
            // After reloading, check again and update
            const projectExistsAfterReload = projects.value.some(p => p.id === projectData.id)
            if (projectExistsAfterReload) {
              selectedProjectId.value = projectData.id
              selectedProject.value = projectData
              console.log('Projeto atualizado após recarregar:', selectedProject.value)
            }
          }
        }
      })

      // Adds a listener for the selected-project-changed event
      eventBus.on('selected-project-changed', async (projectData) => {
        console.log('Evento selected-project-changed recebido:', projectData)

        // If projectData is null, it means the user logged out
        if (projectData === null) {
          console.log('Projeto removido (logout)');
          selectedProjectId.value = '';
          selectedProject.value = null;
          return;
        }

        if (props.isAuthenticated && projectData && projectData.id) {
          // Updates the selected project right away, even if the list is not loaded yet
          selectedProjectId.value = projectData.id
          selectedProject.value = projectData
          console.log('Projeto atualizado imediatamente via evento selected-project-changed:', selectedProject.value)
          
          // If the project list is empty, reload it to make sure the project is in the list
          if (projects.value.length === 0) {
            console.log('Lista de projetos vazia, recarregando...')
            await loadProjects()
            // After reloading, check whether the project is still correctly selected
            const projectExists = projects.value.some(p => p.id === projectData.id)
            if (projectExists) {
              // Confirms the selection
              selectedProjectId.value = projectData.id
              selectedProject.value = projectData
              console.log('Projeto confirmado após recarregar:', selectedProject.value)
            }
          } else {
            // Checks whether the project exists in the project list
            const projectExists = projects.value.some(p => p.id === projectData.id)
            console.log('Projeto existe na lista?', projectExists, 'ID:', projectData.id)
            
            if (!projectExists) {
              // If it is not in the list, reload the projects
              console.log('Projeto não encontrado na lista, recarregando projetos...')
              await loadProjects()
              // After reloading, check again
              const projectExistsAfterReload = projects.value.some(p => p.id === projectData.id)
              if (projectExistsAfterReload) {
                selectedProjectId.value = projectData.id
                selectedProject.value = projectData
                console.log('Projeto confirmado após recarregar:', selectedProject.value)
              }
            }
          }
        }
      })

      // Adds a listener for the dropdown-opened event
      eventBus.on('dropdown-opened', (source) => {
        // Only apply the mutual exclusion on mobile
        if (isMobile.value && source !== 'project' && isProjectDropdownOpen.value) {
          console.log('Outro dropdown foi aberto (mobile), fechando dropdown de projetos')
          isProjectDropdownOpen.value = false
        }
      })
    })

    // Removes the event listeners when the component is unmounted
    const cleanup = () => {
      console.log('Removendo event listeners...')
      document.removeEventListener('click', handleClickOutside)
      window.removeEventListener('resize', handleResize)

      // Removes the storage event listener
      const storageHandler = (event) => {
        if (event.key === 'selectedProject' && props.isAuthenticated) {
          console.log('Projeto alterado no sessionStorage, verificando...')
          checkSessionStorageProject()
        }
      }
      window.removeEventListener('storage', storageHandler)

      // Removes the eventBus listeners
      eventBus.off('project-changed')
      eventBus.off('dropdown-opened')
      eventBus.off('selected-project-changed')

      // Clear session interval if set
      if (sessionIntervalId) {
        clearInterval(sessionIntervalId)
        sessionIntervalId = null
      }
    }

    onUnmounted(cleanup)

    // Computed to check whether there are projects available
    const hasProjects = computed(() => projects.value.length > 0)

    return {
      projects,
      isLoading,
      selectedProjectId,
      selectedProject,
      showConfirmationModal,
      isProjectDropdownOpen,
      projectDropdownRef,
      projectDropdownRefMobile,
      showToast,
      toastMessage,
      hasProjects,
      handleProjectChange,
      toggleProjectDropdown,
      selectProject,

      confirmProjectChange,
      cancelProjectChange,
      showSuccessToast,
      checkSessionStorageProject,
      closeDropdown,
      dropdownMobileStyle
    }
  }
}
</script>

<style scoped>
.project-dropdown {
  position: relative;
}

.project-dropdown .absolute {
  position: absolute;
  z-index: 100;
}

select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

select::-ms-expand {
  display: none;
}

select option {
  background-color: #1E1E1E;
  color: white;
  padding: 8px;
}
</style>
